import { useEffect, useRef, useCallback, useMemo } from 'react';

import config from '../config';
import { getCode } from '../middleware/activate';

/** Event updating director dashboard. */
export const DIRECTOR_DASHBOARD_UPDATED = 'director_dashboard_updated';
/** Event updating project report. */
export const PROJECT_REPORT_UPDATED = 'project_report_updated';

export function useDirectorDashboardPusher(user) {
  const workerRef = useRef(null);
  const listenersRef = useRef(new Map());

  const channelName = useMemo(() => `private-director-dashboard-channel.${user?.get('id')}`, [user]);
  const hasUserId = useMemo(() => !!user?.get('id'), [user]);

  useEffect(() => {
    if (!hasUserId) return;

    initWorker();

    return () => {
      workerRef.current?.port.close();
      listenersRef.current.clear();
    };
  }, [hasUserId]);

  const initWorker = () => {
    const token = getCode();
    const worker = new SharedWorker('/js/shared-pusher-worker.js');

    workerRef.current = worker;
    worker.port.start();

    worker.port.postMessage({
      type: 'init',
      payload: {
        key: config.PUSHER_KEY,
        cluster: config.PUSHER_CLUSTER,
        authEndpoint: `${config.API_BASE_URL}/api/v1/broadcasting/auth`,
        token,
      },
    });

    worker.port.onmessage = messageEvent => {
      const { type, payload } = messageEvent.data;

      if (type === 'reinitialize') {
        refreshToken();
      }

      if (type === 'event') {
        const { channel, event, data } = payload;
        const key = `${channel}:${event}`;
        const listener = listenersRef.current.get(key);

        if (listener) listener(data);
      }
    };
  };

  const refreshToken = async () => {
    const newToken = await getCode();

    workerRef.current?.port.postMessage({
      type: 'updateToken',
      payload: { token: newToken },
    });
  };

  const subscribe = useCallback(
    (eventName, callback) => {
      if (!hasUserId || !workerRef.current) return;

      const key = `${channelName}:${eventName}`;

      listenersRef.current.set(key, callback);

      workerRef.current.port.postMessage({
        type: 'subscribe',
        payload: { channelName },
      });
    },
    [hasUserId],
  );

  const unsubscribe = useCallback(
    eventName => {
      if (!hasUserId) return;

      const key = `${channelName}:${eventName}`;

      listenersRef.current.delete(key);

      workerRef.current?.port.postMessage({
        type: 'unsubscribe',
        payload: { channelName },
      });
    },
    [hasUserId],
  );

  const simulate401 = useCallback(() => {
    workerRef.current?.port.postMessage({
      type: 'simulate401',
      payload: { channelName },
    });
  }, []);

  return { subscribe, unsubscribe, refreshToken, simulate401, channelName };
}
